import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type OnboardingStep = "personal" | "company" | "workshop" | "review" | "complete";

interface OnboardingStatus {
  step: OnboardingStep;
  isComplete: boolean;
  isLoading: boolean;
  profileComplete: boolean;
  hasCompany: boolean;
  hasWorkshop: boolean;
  profile: { firstName: string; lastName: string; phone: string | null } | null;
}

export function useOnboardingStatus(): OnboardingStatus {
  const { user, loading: authLoading } = useAuth();

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ["onboarding-profile", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("first_name, last_name, phone, company_id")
        .eq("id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  // Admin / system_owner users are platform operators — they should never
  // be forced through the company-onboarding wizard.
  const { data: isPlatformAdmin, isLoading: rolesLoading } = useQuery({
    queryKey: ["is-platform-admin", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user!.id)
        .in("role", ["admin", "system_owner"]);
      if (error) throw error;
      return (data ?? []).length > 0;
    },
    enabled: !!user,
  });

  const firstName = profile?.first_name || user?.user_metadata?.first_name || "";
  const lastName = profile?.last_name || user?.user_metadata?.last_name || "";
  const phone = profile?.phone || null;

  const isLoading = authLoading || (!!user && (profileLoading || rolesLoading));
  const profileComplete = !!(firstName && lastName && phone);
  const hasCompany = !!profile?.company_id;

  let step: OnboardingStep = "personal";
  if (isPlatformAdmin) {
    step = "complete";
  } else if (profileComplete && hasCompany) {
    step = "complete";
  } else if (profileComplete) {
    step = "company";
  }

  return {
    step,
    isComplete: step === "complete",
    isLoading,
    profileComplete,
    hasCompany,
    hasWorkshop: hasCompany, // kept for backward compat
    profile: user
      ? {
          firstName,
          lastName,
          phone,
        }
      : null,
  };
}
